import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  FileText, Upload, Sparkles, AlertTriangle, MessageSquare, Loader2,
  Plus, ArrowLeft, Send, CheckCircle, Clock, Rocket, ChevronRight,
  Map as MapIcon, Users, Layers, Megaphone, ShieldCheck, Handshake, Coins, Wallet,
  BookOpen, ChevronDown, ChevronUp, Info, X,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import FeatureGate from '../components/FeatureGate';
import {
  analyzeTokenomicsText, analyzeTokenomicsPdf, iterateTokenomics,
  applyTokenomicsStrategy, listTokenomicsAnalyses, getTokenomicsAnalysis,
} from '../services/api';
import { InfoTip } from '../components/ui';

// ─── Reusable ────────────────────────────────────────────────────────────────

function Card({ children, className = '' }) {
  return <div className={`bg-dark-800 rounded-2xl p-6 border border-dark-700/50 ${className}`}>{children}</div>;
}

function Toast({ message, type = 'success', onDismiss }) {
  useEffect(() => { const t = setTimeout(onDismiss, 4000); return () => clearTimeout(t); }, [onDismiss]);
  return (
    <div className={`fixed bottom-6 right-6 z-50 px-5 py-3 rounded-xl shadow-2xl text-sm font-medium flex items-center gap-2
      ${type === 'success' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : 'bg-red-500/20 text-red-300 border border-red-500/30'}`}>
      {message}
    </div>
  );
}

const SEV_STYLES = {
  high:   { bg: 'bg-red-500/15', text: 'text-red-400', border: 'border-red-500/30' },
  medium: { bg: 'bg-amber-500/15', text: 'text-amber-400', border: 'border-amber-500/30' },
  low:    { bg: 'bg-blue-500/15', text: 'text-blue-400', border: 'border-blue-500/30' },
};

const FOCUS_AREAS = {
  community:    { label: 'Community',    icon: Users,        color: 'violet' },
  product:      { label: 'Product',      icon: Layers,       color: 'cyan' },
  marketing:    { label: 'Marketing',    icon: Megaphone,    color: 'amber' },
  compliance:   { label: 'Compliance',   icon: ShieldCheck,  color: 'blue' },
  partnerships: { label: 'Partnerships', icon: Handshake,    color: 'pink' },
  tokenomics:   { label: 'Tokenomics',   icon: Coins,        color: 'emerald' },
  treasury:     { label: 'Treasury',     icon: Wallet,       color: 'orange' },
};

// ─── About card (collapsible explainer) ────────────────────────────────────

function AboutCard({ open, onToggle }) {
  return (
    <div className="bg-dark-800/60 rounded-2xl border border-dark-700/50 overflow-hidden">
      <button onClick={onToggle} className="w-full px-5 py-4 flex items-center justify-between hover:bg-dark-700/30 transition">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-violet-500/10 border border-violet-500/30 flex items-center justify-center">
            <BookOpen className="w-4 h-4 text-violet-400" />
          </div>
          <div className="text-left">
            <div className="text-sm font-semibold text-white">What is this tool — and how do I use it?</div>
            <div className="text-[11px] text-gray-500">Plain-English walkthrough · 30 seconds</div>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-dark-700/40 pt-4 space-y-4">
          <div>
            <div className="text-xs font-semibold text-violet-300 uppercase tracking-wider mb-1.5">The problem</div>
            <p className="text-sm text-gray-300 leading-relaxed">
              Tokenomics docs are dense — supply, vesting, unlocks, allocations, treasury rules, emission curves. Reading one carefully takes hours; spotting the actual risks (e.g. "team unlock 60 days before your listing") takes deep experience. Most founders ship the whitepaper and never stress-test it.
            </p>
          </div>
          <div>
            <div className="text-xs font-semibold text-violet-300 uppercase tracking-wider mb-1.5">What this tool does</div>
            <p className="text-sm text-gray-300 leading-relaxed">
              Paste your tokenomics text or upload the PDF. <span className="text-white">haythix AI</span> extracts the structure (supply, distribution, vesting), flags the risks with severity + mitigation, and builds a phased <span className="text-white">launch roadmap</span> of real operational milestones tied to your tokenomics calendar — e.g. *"submit Tier-1 CEX application 30 days BEFORE the team unlock to maximize positive press before potential selling pressure"*.
            </p>
          </div>
          <div>
            <div className="text-xs font-semibold text-violet-300 uppercase tracking-wider mb-1.5">How to use it in 3 steps</div>
            <ol className="text-sm text-gray-300 leading-relaxed space-y-1.5 list-decimal pl-5">
              <li>Upload your tokenomics <span className="text-white">PDF</span> or paste the text from your whitepaper / docs site.</li>
              <li>Add the <span className="text-white">token name + contract address</span> (address is optional but helps cross-reference with other tools).</li>
              <li>Click <span className="text-white">Analyze with haythix AI</span>. You'll get a risk list, a phased roadmap, and key dates. Save the roadmap to Launch Roadmap Planner with one click.</li>
            </ol>
          </div>
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 flex items-start gap-2">
            <Info className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-amber-200/90 leading-relaxed">
              <span className="font-semibold">Scope:</span> The roadmap is operational work the team executes themselves (marketing campaigns, listings, audits, partnerships). It deliberately does NOT recommend coordinated trading, multi-wallet activity, or buy/sell ratio manipulation — those are illegal market manipulation under SEC / CFTC / MiCA.
            </p>
          </div>
          <div className="bg-violet-500/5 border border-violet-500/20 rounded-lg p-3 text-[11px] text-violet-200/80 leading-relaxed">
            <span className="font-semibold text-violet-200">Daily limit:</span> 30 AI calls per day per account (combined across analyses + follow-ups). Resets at 00:00 UTC. This protects against runaway costs.
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Premium hero ────────────────────────────────────────────────────────────

function Hero({ onNew, showNew }) {
  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-violet-500/10 via-dark-800/80 to-dark-900 rounded-2xl border border-violet-500/20 p-6">
      <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-violet-400/10 blur-3xl pointer-events-none" />
      <div className="relative flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-violet-500/20 border border-violet-400/30 flex items-center justify-center flex-shrink-0">
            <FileText className="w-6 h-6 text-violet-400" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold text-white">Tokenomics Advisor</h1>
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 font-mono">haythix AI</span>
            </div>
            <p className="text-xs text-violet-200/70 mt-1 flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> Elite tier · Upload tokenomics → extract structure, identify risks, get a real launch roadmap
              <InfoTip side="bottom">30 AI calls/day combined (analyses + follow-ups). Roadmap output is OPERATIONAL only — no coordinated trading. PDF must be text-based; image-only PDFs need paste-text instead.</InfoTip>
            </p>
          </div>
        </div>
        {showNew && onNew && (
          <button onClick={onNew} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-violet-500 text-white hover:bg-violet-400 text-xs font-bold">
            <Plus className="w-3.5 h-3.5" /> New analysis
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Upload View ─────────────────────────────────────────────────────────────

function UploadView({ onAnalyze, loading, error }) {
  const [mode, setMode] = useState('text'); // text | pdf
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [tokenName, setTokenName] = useState('');
  const [tokenAddress, setTokenAddress] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);

  function handleFile(f) {
    if (!f) return;
    if (f.type !== 'application/pdf') return;
    if (f.size > 20 * 1024 * 1024) return;
    setFile(f);
  }

  function submit() {
    if (!tokenName) return;
    if (mode === 'pdf' && file) {
      onAnalyze({ type: 'pdf', file, tokenName, tokenAddress });
    } else if (mode === 'text' && text.trim()) {
      onAnalyze({ type: 'text', text, tokenName, tokenAddress });
    }
  }

  const canSubmit = tokenName && ((mode === 'pdf' && file) || (mode === 'text' && text.trim().length > 50));

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Mode toggle */}
      <Card>
        <div className="flex gap-2 mb-5">
          {[{ id: 'text', label: 'Paste Text', icon: FileText }, { id: 'pdf', label: 'Upload PDF', icon: Upload }].map(m => (
            <button key={m.id} onClick={() => setMode(m.id)}
              className={`flex-1 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all ${
                mode === m.id ? 'bg-accent-primary text-dark-900' : 'bg-dark-700 text-gray-400 hover:text-white border border-dark-600'}`}>
              <m.icon className="w-4 h-4" /> {m.label}
            </button>
          ))}
        </div>

        {mode === 'pdf' ? (
          <div
            className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all ${
              dragOver ? 'border-accent-primary bg-accent-primary/5' : file ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-dark-600 hover:border-dark-500'}`}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
            onClick={() => fileRef.current?.click()}
          >
            <input ref={fileRef} type="file" accept=".pdf" className="hidden" onChange={e => handleFile(e.target.files[0])} />
            {!file ? (
              <>
                <Upload className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <p className="text-white font-medium mb-1">Drop your tokenomics PDF here</p>
                <p className="text-xs text-gray-500">Whitepaper, distribution schedule, vesting plan — up to 20MB</p>
              </>
            ) : (
              <div className="flex items-center justify-center gap-3">
                <FileText className="w-8 h-8 text-emerald-400" />
                <div className="text-left">
                  <p className="text-white font-medium">{file.name}</p>
                  <p className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                </div>
                <button onClick={e => { e.stopPropagation(); setFile(null); }} className="text-gray-500 hover:text-red-400 ml-2">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        ) : (
          <textarea value={text} onChange={e => setText(e.target.value)}
            placeholder="Paste your tokenomics text here — distribution percentages, vesting schedule, allocation breakdown, supply details..."
            rows={8}
            className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-3 text-white text-sm placeholder-gray-600 focus:border-accent-primary focus:outline-none resize-none" />
        )}
      </Card>

      {/* Token info */}
      <Card>
        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">Token Info</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1.5">Token Name *</label>
            <input type="text" value={tokenName} onChange={e => setTokenName(e.target.value)} placeholder="e.g., Your Token"
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-600 focus:border-accent-primary focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1.5">Token Address</label>
            <input type="text" value={tokenAddress} onChange={e => setTokenAddress(e.target.value)} placeholder="0x..."
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm font-mono placeholder-gray-600 focus:border-accent-primary focus:outline-none" />
          </div>
          <div className="flex items-end">
            <button onClick={submit} disabled={!canSubmit || loading}
              className="w-full py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-violet-500 to-indigo-500 text-white hover:from-violet-400 hover:to-indigo-400 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {loading ? 'Analyzing...' : 'Analyze with haythix AI'}
            </button>
          </div>
        </div>
      </Card>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3 text-sm text-red-400 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {error}
        </div>
      )}
    </div>
  );
}

// ─── Analysis Result ─────────────────────────────────────────────────────────

function AnalysisResult({ data, onIterate, onApply, onNew, applying, applied }) {
  const analysis = data.analysis || {};
  const risks = analysis.risks || [];
  const distribution = analysis.tokenomics?.distribution || [];
  // Accept new `roadmap` key, fall back to legacy `strategy` for back-compat
  const roadmap = analysis.roadmap || analysis.strategy || {};
  const phases = roadmap.phases || [];
  const keyDates = roadmap.keyDates || [];

  // For the plain-English summary: pick out the highest-severity risk + the
  // first phase so we can lead with the most actionable signal.
  const highRisks = risks.filter(r => r.severity === 'high');
  const topRisk = highRisks[0] || risks[0];
  const firstPhase = phases[0];
  const totalDays = phases.reduce((s, p) => s + (p.days || 0), 0);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-bold text-white">Analysis Complete</h2>
        <div className="flex gap-2 flex-wrap">
          <button onClick={onIterate}
            className="px-4 py-2 rounded-xl text-xs font-semibold bg-violet-500/15 text-violet-400 border border-violet-500/30 hover:bg-violet-500/25 transition-all flex items-center gap-1.5">
            <MessageSquare className="w-3.5 h-3.5" /> Ask Follow-up
          </button>
          {applied?.roadmapId ? (
            <Link to="/launch-roadmap"
              className="px-4 py-2 rounded-xl text-xs font-semibold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/25 flex items-center gap-1.5">
              <CheckCircle className="w-3.5 h-3.5" /> Roadmap saved — open
            </Link>
          ) : (
            <button onClick={onApply} disabled={phases.length === 0 || applying}
              className="px-4 py-2 rounded-xl text-xs font-semibold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/25 disabled:opacity-40 transition-all flex items-center gap-1.5">
              {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MapIcon className="w-3.5 h-3.5" />}
              Save as Launch Roadmap
            </button>
          )}
          <button onClick={onNew}
            className="px-4 py-2 rounded-xl text-xs font-medium text-gray-400 border border-dark-600 hover:text-white transition-all">
            New Analysis
          </button>
        </div>
      </div>

      {/* What this analysis means — plain-English summary */}
      {(analysis.summary || topRisk || firstPhase) && (
        <div className="bg-gradient-to-br from-violet-500/10 to-indigo-500/5 border border-violet-500/30 rounded-2xl p-5">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-violet-500/20 border border-violet-500/40 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Sparkles className="w-4 h-4 text-violet-300" />
            </div>
            <div className="space-y-2 flex-1">
              <h3 className="text-sm font-bold text-white">What this analysis means for your launch</h3>
              {analysis.summary && (
                <p className="text-xs text-gray-300 leading-relaxed">{analysis.summary}</p>
              )}
              {topRisk && (
                <p className="text-xs text-gray-300 leading-relaxed">
                  <span className={`font-semibold ${topRisk.severity === 'high' ? 'text-red-300' : topRisk.severity === 'medium' ? 'text-amber-300' : 'text-blue-300'}`}>
                    Top risk{topRisk.severity ? ` (${topRisk.severity})` : ''}:
                  </span>{' '}
                  <span className="text-white">{topRisk.title}</span> — {topRisk.description}
                  {topRisk.mitigation && <> <span className="text-emerald-300">Mitigation:</span> {topRisk.mitigation}</>}
                </p>
              )}
              {firstPhase && (
                <p className="text-xs text-gray-300 leading-relaxed">
                  <span className="text-violet-300 font-semibold">Where to start:</span> Phase 1 is <span className="text-white font-semibold">"{firstPhase.name}"</span> over {firstPhase.days} days ({firstPhase.milestones?.length || 0} milestones, {firstPhase.focusArea || 'mixed'} focus). Total roadmap is <span className="font-mono">{totalDays}d / {phases.length} phases / {phases.reduce((s, p) => s + (p.milestones?.length || 0), 0)} milestones</span>.
                </p>
              )}
              {phases.length > 0 && !applied && (
                <p className="text-[11px] text-gray-400 leading-relaxed">
                  Hit <span className="text-white font-semibold">Save as Launch Roadmap</span> to import the phases + milestones into the Launch Roadmap Planner where you can track completion and edit dates.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Distribution */}
      {distribution.length > 0 && (
        <Card>
          <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">Token Distribution</h3>
          <div className="space-y-2">
            {distribution.map((d, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="text-sm text-white font-medium w-32 truncate">{d.category}</span>
                <div className="flex-1 h-2 bg-dark-700 rounded-full overflow-hidden">
                  <div className="h-full bg-accent-primary rounded-full" style={{ width: `${Math.min(d.percentage || 0, 100)}%` }} />
                </div>
                <span className="text-sm text-gray-400 w-12 text-right">{d.percentage}%</span>
                {d.vestingInfo && <span className="text-[10px] text-gray-600 w-40 truncate">{d.vestingInfo}</span>}
              </div>
            ))}
          </div>
          {analysis.tokenomics?.totalSupply && (
            <p className="text-xs text-gray-600 mt-3">Total Supply: {analysis.tokenomics.totalSupply}</p>
          )}
        </Card>
      )}

      {/* Risks */}
      {risks.length > 0 && (
        <Card>
          <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">
            Risks Identified ({risks.length})
          </h3>
          <div className="space-y-3">
            {risks.map((risk, i) => {
              const sev = SEV_STYLES[risk.severity] || SEV_STYLES.medium;
              return (
                <div key={i} className={`rounded-xl px-4 py-3 border ${sev.border} ${sev.bg}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle className={`w-4 h-4 ${sev.text}`} />
                    <span className="text-sm font-semibold text-white">{risk.title}</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${sev.bg} ${sev.text} ml-auto uppercase`}>{risk.severity}</span>
                  </div>
                  <p className="text-xs text-gray-400 mb-1">{risk.description}</p>
                  {risk.date && <p className="text-[10px] text-gray-500">Date: {risk.date}</p>}
                  {risk.mitigation && <p className="text-[10px] text-emerald-400 mt-1">Mitigation: {risk.mitigation}</p>}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Launch Roadmap */}
      {phases.length > 0 && (
        <Card>
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
              <MapIcon className="w-4 h-4 text-violet-400" /> Recommended Launch Roadmap
            </h3>
            <span className="text-[10px] text-gray-500">
              {phases.length} phases · {phases.reduce((s, p) => s + (p.days || 0), 0)} days · {phases.reduce((s, p) => s + (p.milestones?.length || 0), 0)} milestones
            </span>
          </div>
          {roadmap.overview && <p className="text-sm text-gray-400 mb-4">{roadmap.overview}</p>}
          <div className="space-y-3">
            {phases.map((phase, i) => {
              const focus = FOCUS_AREAS[phase.focusArea] || FOCUS_AREAS.marketing;
              const Icon = focus.icon;
              // Tolerate both new (intensityStart/End) and legacy (startValue/endValue) fields
              const intStart = phase.intensityStart ?? phase.startValue ?? 50;
              const intEnd   = phase.intensityEnd   ?? phase.endValue   ?? 50;
              return (
                <div key={i} className="bg-dark-700/30 rounded-xl border border-dark-700/40 overflow-hidden">
                  <div className="p-3 border-b border-dark-700/40 flex items-center gap-3 flex-wrap">
                    <div className={`w-8 h-8 rounded-lg bg-${focus.color}-500/15 border border-${focus.color}-500/30 flex items-center justify-center`}>
                      <Icon className={`w-3.5 h-3.5 text-${focus.color}-400`} />
                    </div>
                    <span className="text-white font-semibold text-sm">{phase.name}</span>
                    <span className="text-[10px] text-gray-500">{phase.days}d</span>
                    <span className="text-[10px] text-gray-500 capitalize">{phase.pattern}</span>
                    <span className="text-[10px] text-gray-400 ml-auto">Intensity {intStart}% → {intEnd}%</span>
                  </div>
                  {phase.milestones?.length > 0 && (
                    <div className="p-3 space-y-1.5">
                      {phase.milestones.map((m, mi) => (
                        <div key={mi} className="flex items-start gap-2 text-xs">
                          <div className="w-1 h-1 rounded-full bg-violet-400 mt-1.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-white">{m.title}</p>
                            <p className="text-[10px] text-gray-500 mt-0.5">
                              Day {Number(m.dueOffsetDays || 0) + 1} of phase
                              {m.owner && <> · {m.owner}</>}
                              {m.rationale && <> · {m.rationale}</>}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Key dates */}
      {keyDates.length > 0 && (
        <Card>
          <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3">Key Dates</h3>
          <div className="space-y-1.5">
            {keyDates.map((kd, i) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                <Clock className="w-3.5 h-3.5 text-gray-600 flex-shrink-0" />
                <span className="text-gray-500 w-24">{kd.date}</span>
                <span className="text-white flex-1">{kd.event}</span>
                <span className="text-xs text-accent-primary">{kd.action}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

// ─── Iterate Chat ────────────────────────────────────────────────────────────

function IterateChat({ analysisId, conversations, onBack }) {
  const [messages, setMessages] = useState(conversations || []);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  async function send() {
    if (!input.trim() || loading) return;
    const q = input.trim();
    setInput('');
    setMessages(prev => [...prev, { question: q, response: null, timestamp: new Date().toISOString() }]);
    setLoading(true);
    try {
      const data = await iterateTokenomics(analysisId, q);
      setMessages(prev => prev.map((m, i) => i === prev.length - 1 ? { ...m, response: data.response } : m));
    } catch {
      setMessages(prev => prev.map((m, i) => i === prev.length - 1 ? { ...m, response: 'Sorry, an error occurred.' } : m));
    }
    setLoading(false);
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <button onClick={onBack} className="text-sm text-gray-500 hover:text-white flex items-center gap-1 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to results
      </button>
      <h2 className="text-lg font-bold text-white">Follow-up Questions</h2>

      <div className="space-y-4 max-h-[50vh] overflow-y-auto">
        {messages.map((m, i) => (
          <div key={i}>
            <div className="flex gap-3 mb-2">
              <div className="w-7 h-7 rounded-lg bg-accent-primary/15 flex items-center justify-center text-[10px] font-bold text-accent-primary flex-shrink-0">You</div>
              <p className="text-sm text-gray-200 pt-1">{m.question}</p>
            </div>
            {m.response && (
              <div className="flex gap-3 ml-10">
                <div className="w-7 h-7 rounded-lg bg-violet-500/15 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-3.5 h-3.5 text-violet-400" />
                </div>
                <div className="prose prose-invert prose-sm max-w-none text-sm">
                  <ReactMarkdown>{m.response}</ReactMarkdown>
                </div>
              </div>
            )}
            {!m.response && loading && i === messages.length - 1 && (
              <div className="ml-10 flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" /> Thinking...
              </div>
            )}
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <div className="flex gap-2">
        <input type="text" value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && send()}
          placeholder="Ask about risks, strategy adjustments, vesting..."
          className="flex-1 bg-dark-700 border border-dark-600 rounded-xl px-4 py-2.5 text-white text-sm placeholder-gray-600 focus:border-violet-500 focus:outline-none" />
        <button onClick={send} disabled={!input.trim() || loading}
          className="p-2.5 rounded-xl bg-violet-500 text-white hover:bg-violet-400 disabled:opacity-30 transition-all">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

// ─── History Sidebar ─────────────────────────────────────────────────────────

function HistoryList({ analyses, onSelect }) {
  if (analyses.length === 0) return null;
  return (
    <Card>
      <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3">Previous Analyses</h3>
      <div className="space-y-2">
        {analyses.map(a => (
          <button key={a.analysisId} onClick={() => onSelect(a.analysisId)}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-sm hover:bg-dark-700 transition-all group">
            <FileText className="w-4 h-4 text-gray-600 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium truncate">{a.tokenName}</p>
              <p className="text-[10px] text-gray-600">{a.riskCount} risks &middot; {a.phaseCount} phases &middot; {new Date(a.createdAt).toLocaleDateString()}</p>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-gray-400" />
          </button>
        ))}
      </div>
    </Card>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function TokenomicsAdvisorPage() {
  const [view, setView] = useState('upload'); // upload | analyzing | result | iterate
  const [analysisData, setAnalysisData] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(null); // { roadmapId } after Save
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [aboutOpen, setAboutOpen] = useState(true);

  async function loadHistory() {
    try {
      const data = await listTokenomicsAnalyses();
      setAnalyses(data?.analyses || []);
    } catch {}
  }

  useEffect(() => { loadHistory(); }, []);

  async function handleAnalyze(input) {
    setLoading(true);
    setError(null);
    setApplied(null);
    setView('analyzing');
    try {
      let result;
      if (input.type === 'pdf') {
        result = await analyzeTokenomicsPdf(input.file, input.tokenName, input.tokenAddress);
      } else {
        result = await analyzeTokenomicsText(input.text, input.tokenName, input.tokenAddress);
      }
      setAnalysisData(result);
      setView('result');
      loadHistory();
    } catch (err) {
      setError(err.message);
      setView('upload');
    }
    setLoading(false);
  }

  async function handleApply() {
    if (!analysisData?.analysisId) return;
    setApplying(true);
    try {
      const roadmap = await applyTokenomicsStrategy(analysisData.analysisId);
      setApplied({ roadmapId: roadmap.roadmapId });
      setToast({ message: 'Saved as Launch Roadmap — open the planner to edit milestones', type: 'success' });
    } catch (err) {
      setToast({ message: `Failed: ${err.message}`, type: 'error' });
    }
    setApplying(false);
  }

  async function loadAnalysis(id) {
    setLoading(true);
    try {
      const data = await getTokenomicsAnalysis(id);
      setAnalysisData(data);
      setView('result');
    } catch {
      setToast({ message: 'Failed to load analysis', type: 'error' });
    }
    setLoading(false);
  }

  return (
    <FeatureGate feature="tokenomicsAdvisor">
      <div className="max-w-7xl mx-auto space-y-6 animate-fade-in">
        {view !== 'iterate' && (
          <Hero
            showNew={view === 'result'}
            onNew={() => { setView('upload'); setAnalysisData(null); setApplied(null); setError(null); }}
          />
        )}

        {view === 'upload' && (
          <AboutCard open={aboutOpen} onToggle={() => setAboutOpen(o => !o)} />
        )}

        {/* Upload */}
        {view === 'upload' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <UploadView onAnalyze={handleAnalyze} loading={loading} error={error} />
            </div>
            <div>
              <HistoryList analyses={analyses} onSelect={loadAnalysis} />
            </div>
          </div>
        )}

        {/* Analyzing */}
        {view === 'analyzing' && (
          <Card className="text-center py-16">
            <div className="relative inline-block mb-5">
              <Sparkles className="w-12 h-12 text-violet-400 animate-pulse" />
            </div>
            <h2 className="text-lg font-bold text-white mb-2">haythix AI is analyzing your tokenomics...</h2>
            <p className="text-sm text-gray-500 mb-6">Extracting vesting schedules, identifying risks, generating a launch roadmap with real milestones</p>
            <div className="flex flex-col gap-2 max-w-xs mx-auto text-left">
              {['Reading document', 'Extracting tokenomics', 'Identifying risks', 'Generating roadmap + milestones'].map((step, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <Loader2 className="w-4 h-4 text-violet-400 animate-spin" />
                  <span className="text-gray-400">{step}</span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Result */}
        {view === 'result' && analysisData && (
          <AnalysisResult
            data={analysisData}
            onIterate={() => setView('iterate')}
            onApply={handleApply}
            onNew={() => { setView('upload'); setAnalysisData(null); setApplied(null); }}
            applying={applying}
            applied={applied}
          />
        )}

        {/* Iterate */}
        {view === 'iterate' && analysisData && (
          <IterateChat
            analysisId={analysisData.analysisId}
            conversations={analysisData.conversations || []}
            onBack={() => setView('result')}
          />
        )}

        {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}
      </div>
    </FeatureGate>
  );
}
